import type { RefObject } from "react";

import type { PolygonsLayerRef } from "@/providers/maps/types";

import type maplibregl from "maplibre-gl";

const EMPTY_FEATURE_COLLECTION: GeoJSON.FeatureCollection<GeoJSON.Polygon> = {
  type: "FeatureCollection",
  features: [],
};

const MAX_START_EDIT_ATTEMPTS = 16;

function toVisibleDraftGeoJson(
  features: GeoJSON.Feature<GeoJSON.Polygon>[],
): GeoJSON.FeatureCollection<GeoJSON.Polygon> {
  const visible = features.filter((f) => !f.properties?.deleted);
  return visible.length > 0
    ? { type: "FeatureCollection", features: visible }
    : EMPTY_FEATURE_COLLECTION;
}

/** Pushes draftFeaturesRef into the MapLibre GeoJSON source without waiting for React render. */
export function syncDraftSourceToMap(
  map: maplibregl.Map | null | undefined,
  draftSourceId: string,
  draftFeatures: GeoJSON.Feature<GeoJSON.Polygon>[],
): void {
  if (!map) return;

  const source = map.getSource(draftSourceId);
  if (source?.type !== "geojson") return;

  (source as maplibregl.GeoJSONSource).setData(toVisibleDraftGeoJson(draftFeatures));
}

export interface StartDraftPolygonEditParams {
  draftLayerRef: RefObject<PolygonsLayerRef | null>;
  tileLayerRef?: RefObject<PolygonsLayerRef | null>;
  boundaryId: string;
  syncDraftToMap?: () => void;
}

/** Same intent as work-map handleTilePolygonClick: upsert draft, then startEdit once layer/source are ready. */
export function startDraftPolygonEditWhenReady({
  draftLayerRef,
  tileLayerRef,
  boundaryId,
  syncDraftToMap,
}: StartDraftPolygonEditParams): void {
  tileLayerRef?.current?.clearActivePolygon();
  syncDraftToMap?.();

  const attemptStart = (attempt: number) => {
    const draftLayer = draftLayerRef.current;

    if (!draftLayer) {
      if (attempt < MAX_START_EDIT_ATTEMPTS) {
        requestAnimationFrame(() => attemptStart(attempt + 1));
      }
      return;
    }

    draftLayer.stopEdit({ endDrawSession: false });
    draftLayer.setActivePolygon({ polygonId: boundaryId });
    syncDraftToMap?.();

    const polygon = draftLayer.getPolygonById(boundaryId);
    if (polygon?.geometry?.type === "Polygon") {
      draftLayer.startEdit(boundaryId);
      return;
    }

    if (attempt < MAX_START_EDIT_ATTEMPTS) {
      requestAnimationFrame(() => attemptStart(attempt + 1));
      return;
    }

    draftLayer.startEdit(boundaryId);
  };

  queueMicrotask(() => {
    requestAnimationFrame(() => attemptStart(0));
  });
}
